/**
 * @file     PhoneVerifyPending.tsx
 * @purpose  SMS code confirmation wall shown after a lead submission that
 *           carried a valid mobile number. Auto-fills from the message via
 *           one-time-code autocomplete, auto-submits at six digits, and lets
 *           the lead correct the number, resend, or fall back to email.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAnalytics } from '@/hooks/useAnalytics';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { formatPhoneInput, toE164 } from '@/utils/formatPhone';

const RESEND_COOLDOWN_S = 30;

export interface PhoneVerifyPendingProps {
  submissionId: string;
  /** E.164 number the first code was sent to. */
  phone: string;
  email: string;
  /** Called once the code is accepted; the lead is verified and drained. */
  onVerified: () => void;
  /** Optional escape hatch back to the email confirmation wall. */
  onUseEmailInstead?: () => void;
}

export function PhoneVerifyPending({
  submissionId,
  phone,
  email,
  onVerified,
  onUseEmailInstead,
}: PhoneVerifyPendingProps) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const { trackEvent } = useAnalytics();

  const [currentPhone, setCurrentPhone] = useState(phone);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const checkedCodes = useRef(new Set<string>());
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    trackEvent({
      eventName: 'lead_phone_confirmation_prompt_viewed',
      eventType: 'funnel',
      category: 'lead_flow',
      metadata: { submission_id: submissionId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submitCode = useCallback(
    async (value: string) => {
      if (checking || verified || checkedCodes.current.has(value)) return;
      checkedCodes.current.add(value);
      setChecking(true);
      setErrorText(null);
      try {
        const res = await secureFetch('/api/leads/verify-phone', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ submissionId, code: value }),
        });
        const json = (await res.json()) as {
          ok?: boolean;
          error?: string;
          session?: { access_token: string; refresh_token: string };
        };
        if (json.ok) {
          // The code signed them in (new account, or their own verified
          // phone): put the session in place so the next page opens signed in.
          if (json.session) {
            const { createSupabaseBrowserClient } = await import('@/services/supabase/client');
            await createSupabaseBrowserClient().auth.setSession(json.session);
          }
          setSignedIn(Boolean(json.session));
          setVerified(true);
          trackEvent({
            eventName: 'lead_phone_confirmed',
            eventType: 'conversion',
            category: 'lead_flow',
            metadata: { submission_id: submissionId },
          });
          window.setTimeout(onVerified, 900);
          return;
        }
        if (json.error === 'invalid_code') {
          setErrorText(
            t('capture.phoneVerify.wrongCode', {
              defaultValue: 'That code did not match. Check the text and try again.',
            }),
          );
        } else if (res.status === 429) {
          setErrorText(
            t('capture.phoneVerify.rateLimited', {
              defaultValue: 'Too many attempts. Wait a moment, then try again.',
            }),
          );
        } else {
          setErrorText(
            t('capture.phoneVerify.checkFailed', {
              defaultValue: 'The code could not be checked. Try again in a moment.',
            }),
          );
        }
      } catch {
        setErrorText(
          t('capture.phoneVerify.checkFailed', {
            defaultValue: 'The code could not be checked. Try again in a moment.',
          }),
        );
      } finally {
        setChecking(false);
      }
    },
    [checking, verified, submissionId, onVerified, t, trackEvent],
  );

  const handleCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setErrorText(null);
    if (digits.length === 6) void submitCode(digits);
  };

  const resend = useCallback(
    async (newPhoneRaw?: string) => {
      setResending(true);
      setErrorText(null);
      try {
        const res = await secureFetch('/api/leads/verify-phone/resend', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            submissionId,
            ...(newPhoneRaw ? { phone: newPhoneRaw } : {}),
          }),
        });
        const json = (await res.json()) as { ok?: boolean; phone?: string; error?: string };
        if (json.ok) {
          if (json.phone) setCurrentPhone(json.phone);
          setEditing(false);
          setCode('');
          checkedCodes.current.clear();
          setCooldown(RESEND_COOLDOWN_S);
          codeInputRef.current?.focus();
        } else if (json.error === 'invalid_phone') {
          setErrorText(
            t('capture.phoneVerify.invalidPhone', {
              defaultValue: 'That number does not look like a mobile number that can receive texts.',
            }),
          );
        } else if (res.status === 429) {
          setErrorText(
            t('capture.phoneVerify.rateLimited', {
              defaultValue: 'Too many attempts. Wait a moment, then try again.',
            }),
          );
        } else {
          setErrorText(
            t('capture.phoneVerify.resendFailed', {
              defaultValue: 'The text could not be sent. Try again in a moment.',
            }),
          );
        }
      } catch {
        setErrorText(
          t('capture.phoneVerify.resendFailed', {
            defaultValue: 'The text could not be sent. Try again in a moment.',
          }),
        );
      } finally {
        setResending(false);
      }
    },
    [submissionId, t],
  );

  const displayPhone = formatPhoneInput(currentPhone.replace(/^\+1/, ''));

  if (verified) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${theme.layout.form.groupGap}px` }}>
        <Typography sx={{ fontSize: 22, lineHeight: '28px', fontWeight: 600, color: theme.colors.textPrimary }}>
          {t('capture.phoneVerify.confirmedHeadline', { defaultValue: 'Number confirmed' })}
        </Typography>
        <Typography sx={{ color: theme.colors.textSecondary, lineHeight: '24px' }}>
          {signedIn
            ? t('capture.phoneVerify.confirmedSignedIn', { defaultValue: 'Your request is sent and you are signed in.' })
            : t('capture.phoneVerify.confirmedBody', {
                defaultValue: 'Your request is sent. A sign-in link was also sent to {{email}}.',
                email,
              })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${theme.layout.form.groupGap}px` }}>
      <Typography sx={{ fontSize: 22, lineHeight: '28px', fontWeight: 600, color: theme.colors.textPrimary }}>
        {t('capture.phoneVerify.headline', { defaultValue: 'Enter the code sent to your phone' })}
      </Typography>
      <Typography sx={{ color: theme.colors.textSecondary, lineHeight: '24px' }}>
        {t('capture.phoneVerify.body', {
          defaultValue: 'A 6-digit code was sent to {{phone}}. Enter it below to confirm your request.',
          phone: displayPhone,
        })}
      </Typography>

      {editing ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            size="small"
            label={t('capture.phoneVerify.editLabel', { defaultValue: 'Mobile number' })}
            value={editValue}
            onChange={(e) => setEditValue(formatPhoneInput(e.target.value))}
            sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: `${theme.radius.md}px` } }}
          />
          <Button
            variant="contained"
            size="medium"
            disabled={resending || !toE164(editValue)}
            onClick={() => void resend(editValue)}
            sx={{ textTransform: 'none' }}
          >
            {resending
              ? t('capture.phoneVerify.sending', { defaultValue: 'Sending…' })
              : t('capture.phoneVerify.sendNewCode', { defaultValue: 'Send code' })}
          </Button>
        </Box>
      ) : (
        <TextField
          inputRef={codeInputRef}
          type="text"
          name="one-time-code"
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus
          label={t('capture.phoneVerify.codeLabel', { defaultValue: '6-digit code' })}
          placeholder="123456"
          value={code}
          error={Boolean(errorText)}
          helperText={errorText}
          onChange={(e) => handleCodeChange(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 6, 'aria-label': 'One-time code' } }}
          InputProps={{
            endAdornment: checking ? <CircularProgress size={18} /> : undefined,
          }}
          sx={{
            maxWidth: 240,
            '& .MuiOutlinedInput-root': { borderRadius: `${theme.radius.md}px` },
            '& input': { letterSpacing: '0.35em', fontSize: 20, fontWeight: 500 },
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {!editing ? (
          <>
            <Button
              variant="text"
              size="small"
              disabled={resending || cooldown > 0}
              onClick={() => void resend()}
              sx={{ textTransform: 'none', p: 0, color: theme.colors.textSecondary }}
            >
              {cooldown > 0
                ? t('capture.phoneVerify.resendIn', {
                    defaultValue: 'Resend code in {{seconds}}s',
                    seconds: cooldown,
                  })
                : t('capture.phoneVerify.resend', { defaultValue: 'Resend code' })}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setEditing(true);
                setEditValue(displayPhone);
              }}
              sx={{ textTransform: 'none', p: 0, color: theme.colors.textSecondary }}
            >
              {t('capture.phoneVerify.editNumber', { defaultValue: 'Wrong number? Edit it' })}
            </Button>
          </>
        ) : (
          <Button
            variant="text"
            size="small"
            onClick={() => setEditing(false)}
            sx={{ textTransform: 'none', p: 0, color: theme.colors.textSecondary }}
          >
            {t('capture.phoneVerify.cancelEdit', { defaultValue: 'Keep {{phone}}', phone: displayPhone })}
          </Button>
        )}
      </Box>

      {onUseEmailInstead ? (
        <Typography sx={{ color: theme.colors.textSecondary, fontSize: 13, lineHeight: '20px' }}>
          {t('capture.phoneVerify.emailFallbackHint', {
            defaultValue: 'No text coming through? A confirmation link was also sent to {{email}}.',
            email,
          })}{' '}
          <Button
            variant="text"
            size="small"
            onClick={onUseEmailInstead}
            sx={{ textTransform: 'none', p: 0, fontSize: 13, verticalAlign: 'baseline' }}
          >
            {t('capture.phoneVerify.useEmailInstead', { defaultValue: 'Confirm by email instead' })}
          </Button>
        </Typography>
      ) : null}
    </Box>
  );
}
